import { TmElement, valueMap } from '@thiememeulenhoff/lit-sdk';
import { html, PropertyValueMap } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { live } from 'lit/directives/live.js';
import { when } from 'lit/directives/when.js';
import { styles, tag } from './tree-item.styles';

@customElement(tag)
export class TreeItemComponent extends TmElement {
  static override styles = [...styles];

  static isTreeItem(node: Node) {
    return node instanceof Element && node.getAttribute('role') === 'treeitem';
  }

  @state() indeterminate = false;

  @state() selectable = false;

  @state() isLeaf = false;

  @property({ type: Boolean, reflect: true })
  expanded = false;

  @property({ type: Boolean, reflect: true })
  selected = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  @query('slot[name=children]')
  childrenSlot!: HTMLSlotElement;

  @property()
  value = '';

  override connectedCallback() {
    super.connectedCallback();

    this.setAttribute('role', 'treeitem');
    this.setAttribute('tabindex', '-1');

    if (this.#isNestedItem()) {
      this.slot = 'children';
    }
  }

  override firstUpdated() {
    this.isLeaf = this.getChildrenItems().length === 0;
  }

  setIndeterminate(indeterminate: boolean) {
    this.indeterminate = indeterminate;
  }

  setSelectable(selectable: boolean) {
    this.selectable = selectable;
  }

  setSelected(selected: boolean) {
    this.selected = selected;
  }

  setExpanded(expanded: boolean) {
    this.expanded = expanded;
  }

  #isNestedItem(): boolean {
    const parent = this.parentElement;
    return !!parent && TreeItemComponent.isTreeItem(parent);
  }

  #handleChildrenSlotChange() {
    this.isLeaf = this.getChildrenItems().length === 0;
  }

  protected override willUpdate(
    changedProperties:
      | PropertyValueMap<TreeItemComponent>
      | Map<PropertyKey, unknown>,
  ) {
    if (
      changedProperties.has('selected') &&
      !changedProperties.has('indeterminate')
    ) {
      this.indeterminate = false;
    }
  }

  getChildrenItems({
    includeDisabled = true,
  }: { includeDisabled?: boolean } = {}) {
    return this.childrenSlot
      ? ([...this.childrenSlot.assignedElements({ flatten: true })].filter(
          (item: Element) =>
            TreeItemComponent.isTreeItem(item as TreeItemComponent) &&
            (includeDisabled || !(item as TreeItemComponent).disabled),
        ) as Array<TreeItemComponent>)
      : [];
  }

  override render() {
    const showExpandButton = !this.isLeaf;

    return html`
      <div
        part="base"
        class="${classMap({
          'tree-item': true,
          'tree-item--expanded': this.expanded,
          'tree-item--selected': this.selected,
          'tree-item--disabled': this.disabled,
          'tree-item--leaf': this.isLeaf,
          'tree-item--has-expand-button': showExpandButton,
        })}"
      >
        <div
          class="tree-item__item"
          part="${valueMap({
            item: true,
            'item--disabled': this.disabled,
            'item--expanded': this.expanded,
            'item--indeterminate': this.indeterminate,
            'item--selected': this.selected,
          })}"
        >
          <div class="tree-item__indentation" part="indentation"></div>

          ${when(
            this.selectable,
            () => html`
              <a-checkbox
                part="checkbox"
                class="tree-item__checkbox"
                ?disabled="${this.disabled}"
                ?checked="${live(this.selected)}"
                ?indeterminate="${this.indeterminate}"
                tabindex="-1"
              ></a-checkbox>
            `,
          )}

          <slot class="tree-item__label" part="label"></slot>

          <div
            part="expand-button"
            class=${classMap({
              'tree-item__expand-button': true,
              'tree-item__expand-button--visible': showExpandButton,
            })}
            aria-hidden="true"
          >
            <a-icon name="chevron-up"></a-icon>
            <a-icon name="chevron-down"></a-icon>
          </div>
        </div>

        <div class="tree-item__children" part="children" role="group">
          <slot
            name="children"
            @slotchange="${this.#handleChildrenSlotChange}"
          ></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tree-item': TreeItemComponent;
  }
}
